import React, {useCallback} from 'react';
import type {BoxProps} from '@tempo/core';
import {Center, DeezerLogo, Divider, Flex, VStack} from '@tempo/core';
import D4CLogo from '@components/D4CLogo';
import UserMenu from '@deezer/next/components/Navigation/UserMenu';
import NavBox from '@components/Sidebar/NavBox';
import {useLogout} from '@deezer/next/contexts/main/hooks';
import {useTranslation} from 'next-i18next';
import {ArrowLeftIcon, ArrowRightIcon, LogoutIcon} from '@tempo/icons';
import {useSidebarContext} from '@components/Sidebar/SidebarProvider';
import NavItem from '@components/Sidebar/NavItem';

type SidebarProps = {
	showFold?: boolean;
	isMobile?: boolean;
	onMenuItemClick?: () => void;
	collapsed?: boolean;
} & BoxProps;

const Sidebar = ({
	showFold,
	isMobile = false,
	onMenuItemClick,
	collapsed,
	...rest
}: SidebarProps) => {
	const {toggleSidebar} = useSidebarContext();
	const {t} = useTranslation('d4c');
	const logout = useLogout();

	const handleLogout = useCallback(async () => {
		await logout({replace: false});
	}, [logout]);
	return (
		<Flex
			direction="column"
			{...rest}
			alignItems="initial"
			height="100vh"
			px="spacing.l"
			py="spacing.xl"
		>
			<VStack
				spacing={{base: 'spacing.l', md: 'spacing.xl'}}
				alignItems="initial"
				height="100%"
				overflow="auto"
			>
				<Flex
					direction="row"
					justifyContent="space-between"
					alignItems="center"
				>
					<D4CLogo
						height="icon.l"
						px="spacing.s"
						opacity={collapsed ? 0 : 1}
						transition="opacity 200ms"
						visibility={collapsed ? 'hidden' : 'visible'}
						position="absolute"
					/>
					<Center width="100%">
						<DeezerLogo
							iconOnly={true}
							height="size.l"
							color="icon.neutral.primary.default"
							opacity={collapsed ? 1 : 0}
							visibility={collapsed ? 'visible' : 'hidden'}
							transition="opacity 200ms"
							sx={{
								'& > path': {
									fill: 'icon.neutral.primary.default',
								},
							}}
						/>
					</Center>
				</Flex>
				<NavBox onMenuItemClick={onMenuItemClick} collapsed={collapsed} />
			</VStack>
			<VStack alignItems="initial" spacing="spacing.xs">
				<NavItem
					label={t('profilesettings_text_logout_d4c-web')}
					icon={LogoutIcon}
					onClick={handleLogout}
					data-testid="sidebar-logout-button"
					collapsed={collapsed}
				/>
				<UserMenu onMenuItemClick={onMenuItemClick} isMobile={isMobile} />
			</VStack>
			{showFold && (
				<>
					<Divider width="100%" my="spacing.l" />
					<NavItem
						label={'Collapse menu'}
						icon={collapsed ? ArrowRightIcon : ArrowLeftIcon}
						onClick={toggleSidebar}
						collapsed={collapsed}
					/>
				</>
			)}
		</Flex>
	);
};

export default Sidebar;
